import apiClient from './apiClient';

/**
 * The V2 plan-engine contract, mirrored from `backend/modules/planEngine/planEngine.service.ts`.
 *
 * Nothing is written by any of these: the onboarding holds its answers in client state and commits
 * once, at the end (PRD 6 §3). `preview` re-runs the same computation the commit will run, so what
 * the athlete validated is what gets saved.
 */

export type PlanType = 'spe' | 'reprise' | 'generic';
/** R40: the cycle's authored role; `RECOVERY` is what trails a specific plan. */
export type CycleRole = 'NORMAL' | 'TEST' | 'RECOVERY';
export type DayMode = 'ATHLETE' | 'DAYS_ONLY' | 'FIXED';
export type Dow = 1 | 2 | 3 | 4 | 5 | 6 | 7;
export type Preparation = 'IDEAL' | 'REDUCED' | 'CONDENSED';

export interface AthleteSettings {
  sessionsPerWeek: Record<string, number>;
  sportDays: Array<{ dow: Dow; sportId: string }>;
  brickDow?: Dow;
  includePpg: boolean;
  includeTests?: boolean;
}

export interface SettingsFormSport {
  sportId: string;
  label: string;
  min: number;
  max: number;
  default: number;
}

export interface SettingsForm {
  planId: string;
  dayMode: DayMode;
  sports: SettingsFormSport[];
  ppg: { available: boolean; sportId: string };
  /** F1: the plan holds at least one test cycle, so the switch means something. */
  tests: { available: boolean };
  hasBricks: boolean;
}

export interface DateBounds {
  condensedFromWeek: string | null;
  reducedFromWeek: string;
  idealFromWeek: string;
  latestWeek: string;
  dminWeeks: number;
  dmaxWeeks: number;
  condensedMinWeeks: number | null;
  recoveryEndsOn: string | null;
}

export interface CycleTimelineRow {
  phase: 'BACKUP' | 'PLAN';
  /** Null on the `BACKUP` row; `RECOVERY` marks what comes after the race. */
  role: CycleRole | null;
  planCycleOrder: number | null;
  cycleId: string | null;
  name: string;
  weeks: number;
}

export interface CycleReduction {
  cycleId: string;
  cycleName: string;
  planCycleOrder: number;
  baselineWeeks: number;
  actualWeeks: number;
}

/** The fixed athlete-facing wording, composed server-side so both apps say the same thing. */
export interface PreparationTexts {
  kind: Preparation | null;
  title: string | null;
  body: string | null;
  reductions: string[];
  closing: string | null;
  testsRemoved: string | null;
}

export interface Recap {
  planId: string;
  planName: string;
  formatCode: string;
  planType: PlanType;
  competitionDate?: string;
  planStartDate: string;
  preparation?: Preparation;
  idealWeeks: number;
  actualWeeks: number;
  reductions: CycleReduction[];
  testsRemoved: number;
  settings?: AthleteSettings;
  /** A range, never a fixed number: a recovery week holds fewer (PRD 6 §7). R43: computed over
   * the weeks that actually hold sessions, so one rest week does not drag the minimum to zero. */
  sessionsPerWeek: { min: number; max: number };
  /** R42/R43: cycle weeks the coach left empty, announced beside the range rather than inside it. */
  restWeeks: number;
  weeklyVolume: Array<{ weekStart: string; phase: 'BACKUP' | 'PLAN'; bySport: Record<string, number> }>;
  weekTemplate: Array<{ dow: Dow; sportIds: string[]; isBrick: boolean }>;
}

export interface PreviewPayload {
  planStartDate: string;
  cycleTimeline: CycleTimelineRow[];
  backup?: { planId: string; planName: string; from: string; to: string };
  /** R41: the trailing recovery cycles of the plan itself, not a plan of its own. */
  recovery?: { from: string; to: string; weeks: number };
  preparation?: Preparation;
  reductions: CycleReduction[];
  testsRemoved: number;
  recap: Recap;
  warnings: Array<{ code: string; count?: number; phase: string; message: string }>;
  messages: { preparation: PreparationTexts; dminWeeks: number; dmaxWeeks: number };
}

export interface EngineErrorBody {
  code: string;
  message?: string;
  sportIds?: string[];
  scenario?: string;
  earliestCompetitionWeek?: string;
  phase?: string;
}

export type CheckSettingsResult =
  | { ok: true; feasibility: unknown }
  | { ok: false; error: EngineErrorBody & { message: string } };

export const getSettingsForm = async (planId: string): Promise<SettingsForm> =>
  (await apiClient.get(`engine/plans/${planId}/settings-form`)).data;

/** R45: the bounds are the same with the tests switch on or off — the plan's length no longer moves. */
export const getDateBounds = async (planId: string): Promise<DateBounds | { bounds: null }> =>
  (await apiClient.get(`engine/plans/${planId}/date-bounds`)).data;

export const checkSettings = async (planId: string, settings: AthleteSettings): Promise<CheckSettingsResult> =>
  (await apiClient.post(`engine/plans/${planId}/check-settings`, { settings })).data;

export const previewPlan = async (
  planId: string,
  competitionDate: string | undefined,
  settings: AthleteSettings
): Promise<PreviewPayload> =>
  (await apiClient.post('engine/preview', { planId, competitionDate, settings })).data;
